import type { ActivitySubject, ActivitySourceResult, AssistantDoneToolCall } from './assistant-done.generated';
import { ASSISTANT_ACTIVITY_SOURCE_KINDS, ASSISTANT_ACTIVITY_SUBJECT_KINDS, ASSISTANT_ACTIVITY_COUNT_UNITS, ASSISTANT_ACTIVITY_STUDY_GOALS } from './assistant-tool-names.generated';

export type AssistantActivitySubject = ActivitySubject;
export type AssistantActivitySourceResult = ActivitySourceResult;
export type AssistantActivitySourceKind = NonNullable<AssistantDoneToolCall['source_kinds']>[number];
export interface AssistantActivityDisplay {
  source_kinds?: Array<AssistantActivitySourceKind>;
  subject?: AssistantActivitySubject;
  sourceResults?: Array<AssistantActivitySourceResult>;
}

export const isAssistantStudyGoalId = (value: unknown): value is NonNullable<ActivitySubject['goalId']> =>
  typeof value === 'string' && (ASSISTANT_ACTIVITY_STUDY_GOALS as readonly string[]).includes(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const inList = (values: readonly string[], value: unknown): value is string =>
  typeof value === 'string' && values.includes(value);

/** The same structural boundary for live frames, saved calls and replay. */
export function projectAssistantActivity(value: unknown): AssistantActivityDisplay {
  if (!isRecord(value)) return {};
  const display: AssistantActivityDisplay = {};
  const kinds = value.source_kinds;
  if (Array.isArray(kinds) && kinds.length && kinds.length <= ASSISTANT_ACTIVITY_SOURCE_KINDS.length &&
      new Set(kinds).size === kinds.length && kinds.every(kind => inList(ASSISTANT_ACTIVITY_SOURCE_KINDS, kind))) {
    display.source_kinds = kinds as Array<AssistantActivitySourceKind>;
  }
  const subject = value.subject;
  if (isRecord(subject) && inList(ASSISTANT_ACTIVITY_SUBJECT_KINDS, subject.kind) &&
      typeof subject.token === 'string' && subject.token.length > 0 && subject.token.length <= 256) {
    display.subject = { kind: subject.kind as ActivitySubject['kind'], token: subject.token,
      ...(typeof subject.name === 'string' && subject.name.trim() && subject.name.length <= 300 ? { name: subject.name } : {}) };
    // goalId is stamped by the authorized database projection, not read from prose.
    if (isAssistantStudyGoalId(subject.goalId)) display.subject.goalId = subject.goalId as ActivitySubject['goalId'];
  }
  if (Array.isArray(value.sourceResults) && value.sourceResults.length <= ASSISTANT_ACTIVITY_COUNT_UNITS.length) {
    const results = value.sourceResults.filter(row => isRecord(row) && inList(ASSISTANT_ACTIVITY_COUNT_UNITS, row.unit) &&
      (row.outcome === 'unavailable' ? row.count == null :
        (row.outcome === 'complete' || row.outcome === 'empty') && Number.isSafeInteger(row.count) &&
        Number(row.count) >= 0 && (row.outcome !== 'empty' || row.count === 0))) as Array<ActivitySourceResult>;
    if (results.length || value.sourceResults.length === 0) {
      display.sourceResults = results.map(row => ({
        unit: row.unit, outcome: row.outcome,
        ...(row.outcome !== 'unavailable' ? { count: row.count } : {}),
      }));
    }
  }
  return display;
}
